<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { analysesApi, type Analysis } from '@/services/api/analyses';
import { useNotifier } from '@/composables/notifier';

const route = useRoute();
const notify = useNotifier();
const analysis = ref<Analysis | null>(null);
const submitting = ref(false);

function ids(): { projectId: string; analysisId: string } {
  const p = route.params.id;
  const a = route.params.analysis_id;
  return {
    projectId: Array.isArray(p) ? (p[0] ?? '') : p ?? '',
    analysisId: Array.isArray(a) ? (a[0] ?? '') : a ?? '',
  };
}

async function reload(): Promise<void> {
  const { projectId, analysisId } = ids();
  analysis.value = await analysesApi.get(projectId, analysisId);
}

async function save(): Promise<void> {
  if (!analysis.value || submitting.value) return;
  submitting.value = true;
  try {
    const { projectId } = ids();
    analysis.value = await analysesApi.update(projectId, analysis.value as Analysis & { id: number });
    notify.info('Analysis saved');
  } finally {
    submitting.value = false;
  }
}

onMounted(reload);
</script>

<template>
  <section v-if="analysis" class="resource">
    <h1>Edit Analysis</h1>
    <form class="resource__form" @submit.prevent="save">
      <label><span>Name</span><input v-model="analysis.name" type="text" required /></label>
      <label><span>Description</span><textarea v-model="analysis.description" rows="4" /></label>
      <button type="submit" :disabled="submitting">Save Analysis</button>
    </form>
  </section>
  <p v-else class="resource">Loading…</p>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
.resource__form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 32rem;
}
.resource__form input,
.resource__form textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}
.resource__form button {
  align-self: flex-start;
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  cursor: pointer;
}
</style>
